import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ClientFormTabs } from '../components/clients/forms/ClientFormTabs';
import { Information } from '../components/clients/forms/tabs/Information';
import { Criteria } from '../components/clients/forms/tabs/Criteria';
import { Matching } from '../components/clients/forms/tabs/Matching';
import { useNavigate, useParams } from 'react-router-dom';
import { useClientFormTabs } from '../hooks/useClientFormTabs';
import { ClientFormProvider } from '../components/clients/forms/contexts/ClientFormContext';
import { clientAPI } from '../api/clients';
import { useAuth } from '../contexts/AuthContext';

export const EditClient: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { activeTab, setActiveTab } = useClientFormTabs();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState<any>(null);

  useEffect(() => {
    const loadClient = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const clients = await clientAPI.getAll({ agency_id: user?.agency_id });
        const client = clients.find((c: any) => c.id === parseInt(id));

        if (!client) {
          alert('Client non trouvé');
          navigate('/clients-actifs');
          return;
        }

        setClientData(client);
      } catch (error) {
        console.error('Erreur lors du chargement du client:', error);
        alert('Erreur lors du chargement du client');
      } finally {
        setLoading(false);
      }
    };

    loadClient();
  }, [id, user, navigate]);

  const handleBack = () => {
    if (activeTab === 'criteria') {
      setActiveTab('information');
    } else {
      navigate('/clients-actifs');
    }
  };

  const handleSave = async (formData: any) => {
    console.log('💾 Début de la mise à jour du client');
    console.log('📋 Données du formulaire:', formData);

    if (!user || !id) {
      alert('Erreur: Utilisateur non connecté ou ID client manquant');
      return;
    }

    if (!formData.civility || !formData.firstName || !formData.lastName || !formData.phone) {
      alert('Veuillez remplir tous les champs obligatoires (civilité, nom, prénom, téléphone)');
      return;
    }

    if (!formData.propertyType || !formData.transactionType) {
      alert('Veuillez sélectionner le type de transaction et le type de bien');
      return;
    }

    try {
      const updateData = {
        civility: formData.civility as 'M.' | 'Mme' | 'Mlle',
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        email: formData.email || undefined,
        address: formData.address,
        wilaya: formData.wilaya,
        city: formData.city,
        notes: formData.notes || undefined,
        transaction_type: formData.transactionType as 'sale' | 'rent' | 'short-rent',
        property_type: formData.propertyType,
        min_budget: parseFloat(formData.minBudget) || 0,
        max_budget: parseFloat(formData.maxBudget) || 0,
        min_area: parseFloat(formData.minArea) || 0,
        min_rooms: parseInt(formData.minRooms) || 1,
        criteria: formData.criteria,
        interest_level: formData.interestLevel as 'very-interested' | 'interested' | 'to-contact',
        agent_id: formData.agent ? parseInt(formData.agent) : undefined,
      };

      console.log('📤 Envoi des données au serveur:', updateData);

      if (!user?.agency_id) {
        throw new Error('Informations d\'authentification manquantes');
      }

      const result = await clientAPI.update(parseInt(id), updateData, user.agency_id);

      console.log('✅ Client mis à jour avec succès:', result);
      alert('Client modifié avec succès !');
      navigate('/clients-actifs');
    } catch (error) {
      console.error('❌ Erreur lors de la modification du client:', error);
      alert('Erreur lors de la modification du client: ' + (error as Error).message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!clientData) {
    return null;
  }

  return (
    <ClientFormProvider initialData={clientData}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center gap-4">
            <Button
              variant="ghost"
              className="p-2"
              onClick={handleBack}
              aria-label="Retour"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Modifier le client
            </h1>
          </div>

          <div className="rounded-lg bg-white shadow-sm dark:bg-gray-800">
            <ClientFormTabs activeTab={activeTab} onTabChange={setActiveTab} />
            <div className="p-6">
              {activeTab === 'information' ? (
                <Information onNext={() => setActiveTab('criteria')} />
              ) : activeTab === 'criteria' ? (
                <Criteria onNext={() => setActiveTab('matching')} />
              ) : (
                <Matching onSave={(formData) => handleSave(formData)} />
              )}
            </div>
          </div>
        </div>
      </div>
    </ClientFormProvider>
  );
};
